import { useEffect, useCallback, useRef } from 'react';
import { useNavigation, OVERLAY_PRIORITY } from './NavigationContext';

/**
 * useMobileBack
 * 
 * Hook for handling mobile back button behavior in PWA.
 * Supports Android hardware back button, iOS swipe-back,
 * and in-app back navigation.
 * 
 * Usage:
 *   useMobileBack({
 *     id: 'cart-panel',
 *     priority: OVERLAY_PRIORITY.CART_SIDEBAR,
 *     isOpen: showCart,
 *     onClose: () => setShowCart(false),
 *   });
 */
export function useMobileBack({ id, priority, isOpen, onClose }) {
  const { registerOverlay, unregisterOverlay } = useNavigation();
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      registerOverlay(id, priority, () => onCloseRef.current());
    } else {
      unregisterOverlay(id);
    }
    return () => {
      unregisterOverlay(id);
    };
    // Only re-run when isOpen or id changes — not on every onClose reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, id, priority, registerOverlay, unregisterOverlay]);
}

/**
 * usePopstateHandler
 * 
 * Low-level hook for components that need direct popstate control.
 * Handles the Android back button at the browser level.
 */
export function usePopstateHandler(isActive, onBack) {
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    if (!isActive) return;

    const handlePopState = (e) => {
      e.preventDefault();
      if (onBackRef.current) {
        onBackRef.current();
      }
      // Re-push state to keep history entry for next back press
      try {
        window.history.pushState({ preventClose: true }, '');
      } catch (err) {
        // SecurityError — safe to ignore
      }
    };

    try {
      window.history.pushState({ preventClose: true }, '');
    } catch (err) {
      // SecurityError — safe to ignore
    }
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isActive]);
}

/**
 * useNavigationGuard
 * 
 * Prevents accidental navigation away from a page with unsaved changes.
 * Shows a confirmation dialog when the user tries to leave.
 * 
 * Usage:
 *   useNavigationGuard({
 *     id: 'checkout-guard',
 *     isActive: isCheckingOut,
 *     message: 'Your order is being placed. Are you sure you want to leave?',
 *   });
 */
export function useNavigationGuard({ id, isActive, message }) {
  const { registerGuard } = useNavigation();

  useEffect(() => {
    if (!isActive) return;

    const unregister = registerGuard(id, () => isActive, message);

    // Also handle browser beforeunload for hard refreshes
    const handleBeforeUnload = (e) => {
      if (isActive) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unregister();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id, isActive, message, registerGuard]);
}

/**
 * usePreventAppClose
 * 
 * Prevents the PWA from closing/minimizing when the user presses back
 * on the home screen. Keeps a history entry so back doesn't exit the app.
 */
export function usePreventAppClose() {
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    // Detect if we're in standalone PWA mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (!isStandalone) return;

    const handlePopState = () => {
      // Always re-push to prevent app from closing
      if (!hasNavigatedRef.current) {
        window.history.pushState({ appRoot: true }, '');
      }
    };

    // Initial push to create a history buffer
    window.history.pushState({ appRoot: true }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const markNavigated = useCallback(() => {
    hasNavigatedRef.current = true;
  }, []);

  const resetNavigated = useCallback(() => {
    hasNavigatedRef.current = false;
  }, []);

  return { markNavigated, resetNavigated };
}

export { OVERLAY_PRIORITY };
